import React, { useState, useEffect } from 'react';
import { Clock, TrendingUp, Zap } from 'lucide-react';
import { formatCurrency, formatDate, formatDurationLong } from '../utils/formatters';
import { Storage } from '../utils/storage';
import { CURRENCIES } from '../constants/currencies';

export default function HistoryPage({ history, setHistory, navigateTo, currency, selectedMeetingId, setSelectedMeetingId }) {
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  useEffect(() => {
    if (selectedMeetingId) {
      const meeting = history.find(m => m.id === selectedMeetingId);
      if (meeting) {
        setSelectedMeeting(meeting);
      }
      setSelectedMeetingId(null);
    }
  }, [selectedMeetingId, history, setSelectedMeetingId]);

  const deleteMeeting = (id) => {
    if (window.confirm('Delete this meeting from history?')) {
      Storage.deleteMeeting(id);
      setHistory(Storage.getHistory());
    }
  };

  const calculateStats = () => {
    if (history.length === 0) return null;
    const totalCost = history.reduce((sum, m) => sum + m.totalCost, 0);
    const totalDurationSeconds = history.reduce((sum, m) => {
      return sum + (m.durationSeconds != null ? m.durationSeconds : (m.duration || 0) * 60);
    }, 0);
    const avgDurationSeconds = totalDurationSeconds / history.length;
    return {
      totalMeetings: history.length,
      totalCost,
      averageCost: totalCost / history.length,
      averageDuration: avgDurationSeconds
    };
  };

  const stats = calculateStats();

  if (history.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <Clock className="w-20 h-20 mx-auto text-gray-400 mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">📊 Meeting History</h1>
          <p className="text-xl text-gray-600 mb-8">No meetings calculated yet!</p>
          <p className="text-gray-500 mb-8">Start tracking your meeting costs to see history and trends.</p>
          <button
            onClick={() => navigateTo('setup')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg"
          >
            Calculate First Meeting
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">📊 Your Meeting History</h1>
            <p className="text-gray-600">Showing your last {history.length} meeting{history.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => navigateTo('home')}
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            ← Back
          </button>
        </div>

        {stats && (
          <div className="bg-purple-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Quick Stats
            </h2>
            <div className="grid md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-purple-600">{stats.totalMeetings}</div>
                <div className="text-sm text-gray-600">Total Meetings</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalCost, currency)}</div>
                <div className="text-sm text-gray-600">Total Cost</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{formatCurrency(stats.averageCost, currency)}</div>
                <div className="text-sm text-gray-600">Average Cost</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{formatDurationLong(stats.averageDuration)}</div>
                <div className="text-sm text-gray-600">Avg Duration</div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4 mb-8">
          {history.map(meeting => {
            const isQuickMode = meeting.participantDetails?.length > 0 && meeting.participantDetails[0].isQuickMode;
            const isFixedDuration = meeting.participantDetails?.length > 0 && meeting.participantDetails[0].isFixedDuration;
            return (
              <div key={meeting.id} className={`border-2 rounded-lg p-6 hover:shadow-md transition-shadow ${
                isQuickMode
                  ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-blue-50' 
                  : isFixedDuration
                  ? 'border-green-300 bg-gradient-to-br from-green-50 to-teal-50'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {isQuickMode && (
                        <span className="inline-flex items-center gap-1 bg-purple-600 text-white px-2 py-1 rounded text-xs font-semibold">
                          <Zap className="w-3 h-3" />
                          Quick
                        </span>
                      )}
                      {isFixedDuration && (
                        <span className="inline-flex items-center gap-1 bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                          <Clock className="w-3 h-3" />
                          Fixed
                        </span>
                      )}
                      <p className="text-sm text-gray-500">{formatDate(meeting.date)}</p>
                    </div>
                    <p className="text-3xl font-bold text-purple-600">{formatCurrency(meeting.totalCost, meeting.currency || currency)}</p>
                    <p className="text-gray-600 mt-1">
                      {formatDurationLong(meeting.durationSeconds || meeting.duration * 60)} • {meeting.participants} people • {formatCurrency(meeting.costPerMinute, meeting.currency || currency)}/min
                    </p>
                  {meeting.comparison && (
                    <p className="text-sm text-gray-700 mt-2 italic">{meeting.comparison}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedMeeting(meeting)}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium"
                >
                  View Details
                </button>
                <button
                  onClick={() => {
                    const isQuickMode = meeting.participantDetails?.length > 0 && meeting.participantDetails[0].isQuickMode;
                    const isFixedDuration = meeting.participantDetails?.length > 0 && meeting.participantDetails[0].isFixedDuration;
                    
                    if (isFixedDuration) {
                      navigateTo('fixed-duration', {
                        mode: meeting.participantDetails.length > 1 && meeting.participantDetails.some(p => p.name !== meeting.participantDetails[0].name) ? 'custom' : 'quick',
                        participantCount: meeting.participantDetails.length,
                        averageSalary: meeting.participantDetails[0].salaryValue,
                        salaryType: meeting.participantDetails[0].salaryType,
                        participants: meeting.participantDetails,
                        hours: Math.floor((meeting.durationSeconds || meeting.duration * 60) / 3600),
                        minutes: Math.floor(((meeting.durationSeconds || meeting.duration * 60) % 3600) / 60)
                      });
                    } else if (isQuickMode) {
                      navigateTo('quick-setup', {
                        participantCount: meeting.participantDetails.length,
                        averageSalary: meeting.participantDetails[0].salaryValue,
                        salaryType: meeting.participantDetails[0].salaryType
                      });
                    } else {
                      navigateTo('setup', { participantDetails: meeting.participantDetails });
                    }
                  }}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium"
                >
                  Calculate Again
                </button>
                <button
                  onClick={() => deleteMeeting(meeting.id)}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {selectedMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setSelectedMeeting(null)}>
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} style={{ maxHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Meeting Details</h2>
              <button
                onClick={() => setSelectedMeeting(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-600">{formatDate(selectedMeeting.date)}</p>
              
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Total Cost</p>
                <p className="text-4xl font-bold text-purple-600">{formatCurrency(selectedMeeting.totalCost, selectedMeeting.currency || currency)}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Duration</p>
                  <p className="text-xl font-semibold">{formatDurationLong(selectedMeeting.durationSeconds || selectedMeeting.duration * 60)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Cost per Minute</p>
                  <p className="text-xl font-semibold">{formatCurrency(selectedMeeting.costPerMinute, selectedMeeting.currency || currency)}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Participants ({selectedMeeting.participantDetails?.length || 0}):</h3>
                
                {selectedMeeting.participantDetails?.length > 0 && selectedMeeting.participantDetails[0].isQuickMode ? (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 border-2 border-purple-200">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <div className="text-4xl">⚡</div>
                      <div>
                        <div className="text-sm text-gray-600 font-medium">Quick Calculate Mode</div>
                        <div className="text-2xl font-bold text-purple-600">
                          {selectedMeeting.participantDetails.length} Participants
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Average Salary:</div>
                      <div className="text-xl font-bold text-gray-900">
                        {selectedMeeting.participantDetails[0].salaryType === 'annual' 
                          ? `${CURRENCIES[selectedMeeting.currency || currency].symbol}${parseInt(selectedMeeting.participantDetails[0].salaryValue).toLocaleString()}/year` 
                          : `${CURRENCIES[selectedMeeting.currency || currency].symbol}${selectedMeeting.participantDetails[0].salaryValue}/hour`}
                      </div>
                    </div>
                  </div>
                ) : selectedMeeting.participantDetails?.length > 0 && selectedMeeting.participantDetails[0].isFixedDuration ? (
                  <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-6 border-2 border-green-200">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <div className="text-4xl">⏱️</div>
                      <div>
                        <div className="text-sm text-gray-600 font-medium">Fixed Duration Mode</div>
                        <div className="text-2xl font-bold text-green-600">
                          {selectedMeeting.participantDetails.length} Participants
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">
                        {selectedMeeting.participantDetails.length === 1 || selectedMeeting.participantDetails.every(p => p.salaryValue === selectedMeeting.participantDetails[0].salaryValue) 
                          ? 'Average Salary:' 
                          : 'Participants:'}
                      </div>
                      {selectedMeeting.participantDetails.length === 1 || selectedMeeting.participantDetails.every(p => p.salaryValue === selectedMeeting.participantDetails[0].salaryValue) ? (
                        <div className="text-xl font-bold text-gray-900">
                          {selectedMeeting.participantDetails[0].salaryType === 'annual' 
                            ? `${CURRENCIES[selectedMeeting.currency || currency].symbol}${parseInt(selectedMeeting.participantDetails[0].salaryValue).toLocaleString()}/year` 
                            : `${CURRENCIES[selectedMeeting.currency || currency].symbol}${selectedMeeting.participantDetails[0].salaryValue}/hour`}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {selectedMeeting.participantDetails.map((p, i) => (
                            <div key={i} className="flex justify-between items-center bg-gray-50 rounded p-2 text-xs">
                              <span className="font-medium truncate mr-2">{p.name || `Person ${i + 1}`}</span>
                              <span className="text-gray-600 whitespace-nowrap">
                                {p.salaryType === 'annual' 
                                  ? `${CURRENCIES[selectedMeeting.currency || currency].symbol}${parseInt(p.salaryValue).toLocaleString()}/yr` 
                                  : `${CURRENCIES[selectedMeeting.currency || currency].symbol}${p.salaryValue}/hr`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-2">
                    {selectedMeeting.participantDetails?.map((p, i) => {
                      return (
                        <div key={i} className="flex justify-between items-center bg-gray-50 rounded-lg p-3 text-sm">
                          <span className="font-medium truncate mr-2">
                            {p.name || `Person ${i + 1}`}
                          </span>
                          <span className="text-gray-600 text-xs whitespace-nowrap">
                            {p.salaryType === 'annual' 
                              ? `${CURRENCIES[selectedMeeting.currency || currency].symbol}${parseInt(p.salaryValue).toLocaleString()}/yr` 
                              : `${CURRENCIES[selectedMeeting.currency || currency].symbol}${p.salaryValue}/hr`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedMeeting.comparison && (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <p className="text-lg font-semibold text-gray-900">{selectedMeeting.comparison}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    setSelectedMeeting(null);
                    navigateTo('results', selectedMeeting);
                  }}
                  className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700"
                >
                  Share These Results
                </button>
                <button
                  onClick={() => {
                    setSelectedMeeting(null);
                    const isQuickMode = selectedMeeting.participantDetails?.length > 0 && selectedMeeting.participantDetails[0].isQuickMode;
                    const isFixedDuration = selectedMeeting.participantDetails?.length > 0 && selectedMeeting.participantDetails[0].isFixedDuration;
                    
                    if (isFixedDuration) {
                      navigateTo('fixed-duration', {
                        mode: selectedMeeting.participantDetails.length > 1 && selectedMeeting.participantDetails.some(p => p.name !== selectedMeeting.participantDetails[0].name) ? 'custom' : 'quick',
                        participantCount: selectedMeeting.participantDetails.length,
                        averageSalary: selectedMeeting.participantDetails[0].salaryValue,
                        salaryType: selectedMeeting.participantDetails[0].salaryType,
                        participants: selectedMeeting.participantDetails,
                        hours: Math.floor((selectedMeeting.durationSeconds || selectedMeeting.duration * 60) / 3600),
                        minutes: Math.floor(((selectedMeeting.durationSeconds || selectedMeeting.duration * 60) % 3600) / 60)
                      });
                    } else if (isQuickMode) {
                      navigateTo('quick-setup', {
                        participantCount: selectedMeeting.participantDetails.length,
                        averageSalary: selectedMeeting.participantDetails[0].salaryValue,
                        salaryType: selectedMeeting.participantDetails[0].salaryType
                      });
                    } else {
                      navigateTo('setup', { participantDetails: selectedMeeting.participantDetails });
                    }
                  }}
                  className="flex-1 border-2 border-purple-600 text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50"
                >
                  Calculate Similar
                </button>
              </div>

              <button
                onClick={() => setSelectedMeeting(null)}
                className="w-full text-gray-600 px-6 py-3 rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}